import React, { createContext, useContext } from 'react';
import Axios from 'axios';
import { AppConfigContext, axios_timeout } from '../Config/App';
import { getUserFromLocalStorage } from '../utils/localStorage';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { LanguageContext } from '../Data/Context/languageContext';

export const DatabaseContext = createContext();

export const DatabaseProvider = ({ children }) => {
    const { api_url, uri_segment, navigate_to } = useContext(AppConfigContext);
    const user_session = getUserFromLocalStorage();
    const navigate = useNavigate();
    const success = 'success';
    const error = 'error';
    const info = 'info';
    const { t } = useContext(LanguageContext);
    let endpoint;

    const successToast = () => {
        let text = '';
        if (!isNaN(uri_segment())) {
            text = t('updated');
        } else {
            text = t('created');
        }

        toast.success(t('build') + ' ' + text + ' ' + t('successfully'), {
            toastId: success,
            autoClose: 1500,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            draggable: false,
            closeOnClick: false,
        });

        setTimeout(() => {
            navigate(navigate_to('dashboard'));
        }, 500);
    };

    const errorToast = () => {
        toast.error(t('toast_unexpected_error'), {
            toastId: error,
            autoClose: false,
        });
    };

    const deleteToast = () => {
        toast.info(t('build') + ' ' + t('removed') + ' ' + t('successfully'), {
            toastId: info,
            autoClose: 2500,
            pauseOnHover: false,
            pauseOnFocusLoss: false,
            draggable: false,
            closeOnClick: false,
        });
        navigate(navigate_to('dashboard'));
    };

    function processData(payload, CRUD) {
        if (CRUD === 'create') {
            endpoint = api_url('save-build');
        } else if (CRUD === 'update') {
            endpoint = api_url(`edit-build/${uri_segment()}`);
        }

        if (CRUD === 'create') createBuild(payload);

        if (CRUD === 'update') updateBuild(payload);

        if (CRUD === 'delete') {
            Axios.delete(api_url(`delete-build/${user_session.user_id}/${payload.build_id}/${user_session.token}`), {
                timeout: axios_timeout,
            })
                .then((response) => {
                    if (response.data) deleteToast();
                    else errorToast();
                })
                .catch((err) => {
                    errorToast();
                    console.log(err.code, err.stack, err.message);
                });
        }
    }

    function updateBuild(processedData) {
        Axios.put(endpoint, {
            token: user_session.token,
            user_id: user_session.user_id,
            username: user_session.username,
            title: processedData.title,
            pokemon_id: processedData.id,
            name: processedData.name,
            picture: processedData.picture,
            ability_1: processedData.abilities.ability_1,
            ability_2: processedData.abilities.ability_2,
            ability_3: processedData.abilities.ability_3,
            ability_4: processedData.abilities.ability_4,
            ability_5: processedData.abilities.ability_5,
            ability_6: processedData.abilities.ability_6,
            ability_7: processedData.abilities.ability_7,
            ability_8: processedData.abilities.ability_8,
            held_item_1: processedData.heldItems.held_item_1,
            held_item_2: processedData.heldItems.held_item_2,
            held_item_3: processedData.heldItems.held_item_3,
            held_item_4: processedData.heldItems.held_item_4,
            gear_1: processedData.gear.gear_1,
            gear_2: processedData.gear.gear_2,
            gear_3: processedData.gear.gear_3,
            gear_4: processedData.gear.gear_4,
            personality_1: processedData.personalities.personality_1,
            personality_2: processedData.personalities.personality_2,
            personality_3: processedData.personalities.personality_3,
            personality_4: processedData.personalities.personality_4,
            teammate_1: processedData.teammates.teammate_1,
            teammate_2: processedData.teammates.teammate_2,
            teammate_3: processedData.teammates.teammate_3,
            teammate_4: processedData.teammates.teammate_4,
            hidden_skill_1: processedData.hiddenSkills.hidden_skill_1,
            hidden_skill_2: processedData.hiddenSkills.hidden_skill_2,
            hidden_skill_3: processedData.hiddenSkills.hidden_skill_3,
            hidden_skill_4: processedData.hiddenSkills.hidden_skill_4,
            hidden_skill_5: processedData.hiddenSkills.hidden_skill_5,
            hidden_skill_6: processedData.hiddenSkills.hidden_skill_6,
            hidden_skill_7: processedData.hiddenSkills.hidden_skill_7,
            hidden_skill_8: processedData.hiddenSkills.hidden_skill_8,
            hidden_skill_9: processedData.hiddenSkills.hidden_skill_9,
            hidden_skill_10: processedData.hiddenSkills.hidden_skill_10,
            hidden_skill_11: processedData.hiddenSkills.hidden_skill_11,
            hidden_skill_12: processedData.hiddenSkills.hidden_skill_12,
            generalNotes: processedData.notes.general_notes,
            abilitiesNotes: processedData.notes.abilities_notes,
            personalitiesNotes: processedData.notes.personalities_notes,
            heldItemsNotes: processedData.notes.held_items_notes,
            gearNotes: processedData.notes.gear_notes,
            hiddenSkillsNotes: processedData.notes.hidden_skills_notes,
            gameVersion: processedData.gameVersion,
        })
            .then((response) => {
                if (response.status === 200) {
                    successToast();
                } else {
                    errorToast();
                }
            })
            .catch((err) => {
                console.log(err.code, err.stack, err.message);
            });
    }

    function createBuild(processedData) {
        Axios.post(endpoint, {
            token: user_session.token,
            user_id: user_session.user_id,
            username: user_session.username,
            title: processedData.title,
            pokemon_id: processedData.id,
            name: processedData.name,
            picture: processedData.picture,
            ability_1: processedData.abilities.ability_1,
            ability_2: processedData.abilities.ability_2,
            ability_3: processedData.abilities.ability_3,
            ability_4: processedData.abilities.ability_4,
            ability_5: processedData.abilities.ability_5,
            ability_6: processedData.abilities.ability_6,
            ability_7: processedData.abilities.ability_7,
            ability_8: processedData.abilities.ability_8,
            held_item_1: processedData.heldItems[0],
            held_item_2: processedData.heldItems[1],
            held_item_3: processedData.heldItems[2],
            held_item_4: processedData.heldItems[3],
            gear_1: processedData.gear[0],
            gear_2: processedData.gear[1],
            gear_3: processedData.gear[2],
            gear_4: processedData.gear[3],
            personality_1: processedData.personalities[0],
            personality_2: processedData.personalities[1],
            personality_3: processedData.personalities[2],
            personality_4: processedData.personalities[3],
            teammate_1: processedData.teammates.teammate_2,
            teammate_2: processedData.teammates.teammate_3,
            teammate_3: processedData.teammates.teammate_5,
            teammate_4: processedData.teammates.teammate_6,
            hidden_skill_1: processedData.hiddenSkills[0],
            hidden_skill_2: processedData.hiddenSkills[1],
            hidden_skill_3: processedData.hiddenSkills[2],
            hidden_skill_4: processedData.hiddenSkills[3],
            hidden_skill_5: processedData.hiddenSkills[4],
            hidden_skill_6: processedData.hiddenSkills[5],
            hidden_skill_7: processedData.hiddenSkills[6],
            hidden_skill_8: processedData.hiddenSkills[7],
            hidden_skill_9: processedData.hiddenSkills[8],
            hidden_skill_10: processedData.hiddenSkills[9],
            hidden_skill_11: processedData.hiddenSkills[10],
            hidden_skill_12: processedData.hiddenSkills[11],
            generalNotes: processedData.notes.general,
            abilitiesNotes: processedData.notes.abilities,
            personalitiesNotes: processedData.notes.personalities,
            heldItemsNotes: processedData.notes.heldItems,
            gearNotes: processedData.notes.gear,
            hiddenSkillsNotes: processedData.notes.hiddenSkills,
            gameVersion: processedData.gameVersion,
        })
            .then((response) => {
                if (response.status === 201) {
                    successToast();
                } else {
                    errorToast();
                }
            })
            .catch((err) => {
                console.log(err.code, err.stack, err.message);
            });
    }

    function castVote(vote, build_id, clear_vote) {
        let like = 0;
        let dislike = 0;

        vote ? (like = 1) : (dislike = 1);

        Axios.post(
            api_url('vote-build'),
            {
                user_id: user_session.user_id,
                build_id: build_id,
                token: user_session.token,
                like: like,
                dislike: dislike,
                clear_vote: clear_vote,
            },
            { timeout: axios_timeout }
        ).catch((err) => {
            console.log(err.code, err.stack, err.message);
        });
    }

    return (
        <DatabaseContext.Provider value={{ createBuild, updateBuild, processData, castVote }}>
            {children}
        </DatabaseContext.Provider>
    );
};
